import { X } from 'lucide-react';
import { useEffect } from 'react';

interface MonthlyBreakdown {
    month: number;
    year: number;
    points_given: number;
    points_earned: number;
}

interface PeerPointsModalProps {
    isOpen: boolean;
    onClose: () => void;
    monthlyBreakdown: MonthlyBreakdown[];
    type: 'given' | 'received';
}

const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
];

const PeerPointsModal = ({ isOpen, onClose, monthlyBreakdown, type }: PeerPointsModalProps) => {
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isOpen]);

    if (!isOpen) return null;

    const title = type === 'given' ? 'Points Given' : 'Points Received';
    const pointsKey = type === 'given' ? 'points_given' : 'points_earned';

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
            {/* Backdrop */}
            <div 
                className="absolute inset-0 bg-black bg-opacity-50"
                onClick={onClose}
            />
            
            {/* Modal */}
            <div className="relative bg-white dark:bg-gray-800 max-w-[775px] rounded-lg shadow-xl w-full mx-4 max-h-[80vh] overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-surface-border">
                    <h2 className=" mb-0 my-0 font-bold text-xl-dv text-black dark:text-white">
                        {title} - Monthly Breakdown
                    </h2>
                    <button
                        onClick={onClose}
                        className="text-gray-400 cta-red transition mt-0.5 ml-4 flex-shrink-0 pt-1"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path></svg>
                    </button>
                </div>

                {/* Content */}
                <div className="p-6 overflow-y-auto max-h-[calc(80vh-120px)]">
                    {monthlyBreakdown.length === 0 ? (
                        <p className="text-center text-text-text-secondary py-8">
                            No data available for the selected period
                        </p>
                    ) : (
                        <div className="space-y-3">
                            {monthlyBreakdown.map((item, index) => (
                                <div
                                    key={`${item.year}-${item.month}-${index}`}
                                    className="flex justify-between items-center p-4 bg-surface-bg-secondary rounded-lg hover:bg-surface-border transition-colors"
                                >
                                    <span className="text-sm font-medium text-text-primary dark:text-white">
                                        {monthNames[item.month - 1]} {item.year}
                                    </span>
                                    <span className={`text-lg font-semibold ${
                                        type === 'given' ? 'text-main-blue' : 'text-success-active'
                                    }`}>
                                        {item[pointsKey]}
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PeerPointsModal;
